import type { ObjectMetadata } from '@olytic/foundation';

import { formatNumber, formatPercent } from '../../objects/format';
import { OBJECT_DEFS } from '../../objects/objectDefs';
import {
  activityColumns, asCheck, asDate, asDateTime, asLabel, assignmentColumns, column, createdByField, documentColumns, lastModifiedByField, listColumnsOf, lookup, meetingColumns,
  related, type Row, schemaRecord, show, text,
} from './kit';

const def = OBJECT_DEFS.projects;
const asHours = show((v) => formatNumber(v, 1));

/**
 * Its fields come from the database (the server's SCHEMA_OBJECTS), shown by
 * their type, the Account linked to its record; any column not placed below
 * appears in Other Fields (MOTHER-296). The hours are the record's roll-up.
 */
const field = schemaRecord('projects');

const details = [
  field('name', 'Project Name'),
  field('code', 'Code'),
  field('status', 'Status'),
  field('account_name', 'Account'),
  // A person, edited like any lookup; a name, not a code.
  field('owner_name', 'Owner', { format: text, link: lookup('people', 'owner_id') }),
  field('billable_hours', 'Billable Hours', { format: asHours }),
  field('non_billable_hours', 'Non-Billable Hours', { format: asHours }),
  field('forecast_hours', 'Forecast Hours', { format: asHours }),
  field('billable_of_forecast', 'Billable of Forecast', { format: show(formatPercent) }),
  field('created_at', 'Created'),
  field('updated_at', 'Last Modified'),
  createdByField(),
  lastModifiedByField(),
];

/** A project: client work that time is logged against. Rationale: projects.md. */
export const projects: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Project',
  plural: def.label,
  icon: def.icon,
  listColumns: listColumnsOf(def, details),
  details,
  sections: [
    { key: 'identity', fields: ['name', 'code', 'status', 'account_name', 'owner_name'] },
    { key: 'hours', label: 'Hours', fields: ['billable_hours', 'non_billable_hours', 'forecast_hours', 'billable_of_forecast'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_at', 'created_by_name', 'updated_at', 'last_modified_by_name'] },
  ],
  related: [
    related('time-entries', 'Time Entries', 'time-entries', [
      column('entry_date', 'Date', { primary: true, format: asDate }),
      column('owner_name', 'Person'),
      column('hours', 'Hours', { format: show((v) => formatNumber(v)), align: 'right' }),
    ]),
    related('resource-assignments', 'Resource Assignments', 'resource-assignments', assignmentColumns()),
    related('meetings', 'Meetings', 'meetings', [...meetingColumns().slice(0, 2), column('client_health', 'Health', { format: asLabel })]),
    related('activities', 'Activities', 'activities', activityColumns().slice(0, 3)),
    related('documents', 'Documents', 'documents', [documentColumns()[0]!, documentColumns()[2]!]),
    related('deliverables', 'Deliverables', 'deliverables', [
      column('name', 'Deliverable', { primary: true }),
      column('status', 'Status', { format: asLabel }),
      { ...column('is_exemplar', 'Exemplar'), render: (row) => asCheck(row.is_exemplar) },
      column('updated_at', 'Last Modified', { format: asDateTime }),
    ]),
  ],
  // Five related lists cross the rail's limit (projects.md): one tab per job.
  tabs: [
    { key: 'information', label: 'Information', details: true },
    { key: 'delivery', label: 'Delivery', related: ['time-entries', 'resource-assignments', 'meetings'] },
    // History, not Activity (MOTHER-243): the Activity tab is the timeline ObjectRecordView adds after Delivery.
    { key: 'history', label: 'History', related: ['activities', 'documents', 'deliverables'] },
  ],
  titleOf: (row) => text(row.name),
  searchKeys: ['name', 'code', 'account_name'],
};
